import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Checkbox,
  FormControlLabel,
  Chip,
  Button,
  useTheme,
  Tooltip,
  Collapse,
  Divider,
  Paper,
  alpha,
  useMediaQuery,
  Stack,
  IconButton,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import CategoryIcon from '@mui/icons-material/Category';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { CheckCircleOutline as CheckCircleOutlineIcon } from '../../icons';
import { r } from '../../theme/tokens';

// Only keep constants that represent fixed schema values
export const FORM_FACTORS = [
  'Capsules',
  'Chewables',
  'Liquid',
  'Powder',
  'Softgels',
  'Tablets'
].sort();

export const DIETARY_OPTIONS = [
  'Gluten-Free',
  'Non-GMO',
  'Soy-Free',
  'Vegetarian/Vegan'
];

// Map UI-friendly names to database field names
export const DIETARY_FIELD_MAP = {
  'Gluten-Free': 'gluten_free',
  'Non-GMO': 'non_gmo',
  'Soy-Free': 'soy_free',
  'Vegetarian/Vegan': 'vegetarian',
};

// Create a mapping of standard form factors to all possible variations
export const FORM_FACTOR_MAPPINGS = {
  'capsules': [
    'capsule', 'capsules', 'cap', 'caps', 'vegcap', 'vegetarian capsule', 
    'vcap', 'vegetable capsule', 'plant capsule'
  ],
  'chewables': [
    'chewable', 'chewables', 'chew', 'chews', 'chewy', 'soft chew', 'gummy', 'gummies'
  ],
  'liquid': [
    'liquid', 'liquids', 'solution', 'tincture', 'extract', 'drops', 
    'oil', 'oils', 'liquid extract', 'fluid', 'syrup', 'concentrate',
    'spray', 'oral spray', 'liquid form', 'dropper', 'elixir', 'serum',
    'suspension', 'sublingual', 'sublingual drops', 'sublingual liquid',
    'oral solution', 'oral liquid', 'tonic', 'liquid concentrate'
  ],
  'powder': [
    'powder', 'powders', 'pwdr', 'pwd', 'powder form', 'powdered', 
    'loose powder', 'bulk powder', 'drink mix', 'micronized powder'
  ],
  'softgels': [
    'softgel', 'softgels', 'soft gel', 'soft gels', 'gelcap', 'gelcaps',
    'gel cap', 'gel caps', 'soft gelatin capsule', 'liquid capsule'
  ],
  'tablets': [
    'tablet', 'tablets', 'tab', 'tabs', 'caplet', 'caplets'
  ],
};

// Display form factors with more helpful descriptions in the UI
export const FORM_FACTOR_DISPLAY = {
  'Capsules': 'Capsules (incl. veggie caps)',
  'Chewables': 'Chewables & Gummies',
  'Liquid': 'Liquid (incl. oils, tinctures)',
  'Powder': 'Powder (incl. drink mixes)',
  'Softgels': 'Softgels (incl. gel caps)',
  'Tablets': 'Tablets (incl. caplets)'
};

const ProductFilters = ({
  filters,
  onFilterChange,
  onResetFilters,
  filterCount,
  onClose,
  userPreferences = {},
  hideFilterChips = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Check if onClose exists before using it
  const handleClose = () => {
    if (typeof onClose === 'function') {
      onClose();
    }
  };

  // Handle filter changes
  const handleFilterChange = (event) => {
    const { name, checked, value, type } = event.target;
    
    if (name === 'dietaryOptions') {
      // For dietary options
      const fieldName = DIETARY_FIELD_MAP[value];
      onFilterChange({
        ...filters,
        [fieldName]: checked
      });
    } else if (name === 'formFactors') {
      // For form factor multi-select
      const currentValues = filters.formFactors || [];
      onFilterChange({
        ...filters,
        formFactors: checked
          ? [...currentValues, value]
          : currentValues.filter(option => option !== value)
      });
    } else {
      // For boolean checkboxes (Quality Assurance)
      onFilterChange({
        ...filters,
        [name]: checked
      });
    }
  };

  // Helper function to get tooltip text for form factor variants
  const getFormFactorVariants = (form) => {
    // Find the canonical key (e.g., 'Chewables' -> 'chewables')
    const canonicalKey = Object.keys(FORM_FACTOR_DISPLAY).find(
      key => FORM_FACTOR_DISPLAY[key] === FORM_FACTOR_DISPLAY[form]
    )?.toLowerCase();

    if (!canonicalKey) return 'No variations listed';

    const variations = FORM_FACTOR_MAPPINGS[canonicalKey] || [];
    return variations.slice(0, 4).join(', ') + (variations.length > 4 ? '...' : '');
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        borderRadius: '0.625rem',
        overflow: 'hidden',
        mb: { xs: 2, sm: 3 },
        border: `0.0625rem solid ${alpha(theme.palette.primary.main, 0.1)}`,
        boxShadow: `0 0.25rem 0.75rem ${alpha(theme.palette.common.black, 0.04)}`,
        transition: 'all 0.3s ease',
        position: 'relative',
        display: 'block', // Ensure it's visible when used
      }}
    >
      {/* Active filters display - only show if not hidden by parent */}
      {filterCount > 0 && !hideFilterChips && (
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 1, 
          p: { xs: 1.5, sm: 2 },
          width: '100%',
          backgroundColor: alpha(theme.palette.primary.main, 0.04),
          borderBottom: `${r(1)} solid ${alpha(theme.palette.primary.main, 0.1)}`,
        }}>
          {/* Quality filters */}
          {filters.third_party_tested && (
            <Chip
              label="Third-Party Tested"
              size="small"
              onDelete={() => onFilterChange({ target: { name: 'third_party_tested', checked: false } })}
              color="primary"
              variant="outlined"
              sx={{ 
                borderRadius: '0.375rem',
                height: { xs: '1.5rem', sm: '1.75rem' },
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                '& .MuiChip-deleteIcon': {
                  color: theme.palette.primary.main,
                  width: { xs: '1rem', sm: '1.125rem' },
                  height: { xs: '1rem', sm: '1.125rem' },
                },
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
                '& .MuiChip-label': {
                  px: { xs: 1, sm: 1.2 },
                }
              }}
            />
          )}
          
          {filters.gmp_certified_facility && (
            <Chip
              label="GMP Certified"
              size="small"
              onDelete={() => onFilterChange({ target: { name: 'gmp_certified_facility', checked: false } })}
              color="primary"
              variant="outlined"
              sx={{ 
                borderRadius: '0.375rem',
                height: { xs: '1.5rem', sm: '1.75rem' },
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                '& .MuiChip-deleteIcon': {
                  color: theme.palette.primary.main,
                  width: { xs: '1rem', sm: '1.125rem' },
                  height: { xs: '1rem', sm: '1.125rem' },
                },
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
                '& .MuiChip-label': {
                  px: { xs: 1, sm: 1.2 },
                }
              }}
            />
          )}
          
          {filters.has_certifications && (
            <Chip
              label="Has Certifications"
              size="small"
              onDelete={() => onFilterChange({ target: { name: 'has_certifications', checked: false } })}
              color="primary"
              variant="outlined"
              sx={{ 
                borderRadius: '0.375rem',
                height: { xs: '1.5rem', sm: '1.75rem' },
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                '& .MuiChip-deleteIcon': {
                  color: theme.palette.primary.main,
                  width: { xs: '1rem', sm: '1.125rem' },
                  height: { xs: '1rem', sm: '1.125rem' },
                },
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
                '& .MuiChip-label': {
                  px: { xs: 1, sm: 1.2 },
                }
              }}
            />
          )}
          
          {/* Dietary filters */}
          {Object.entries(DIETARY_FIELD_MAP).map(([label, fieldName]) => (
            filters[fieldName] && (
              <Chip
                key={fieldName}
                label={label}
                size="small"
                onDelete={() => onFilterChange({
                  target: { name: 'dietaryOptions', checked: false, value: label }
                })}
                color="primary"
                variant="outlined"
                sx={{ 
                  borderRadius: '0.375rem',
                  height: { xs: '1.5rem', sm: '1.75rem' },
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  '& .MuiChip-deleteIcon': {
                    color: theme.palette.primary.main,
                    width: { xs: '1rem', sm: '1.125rem' },
                    height: { xs: '1rem', sm: '1.125rem' },
                  },
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  },
                  '& .MuiChip-label': {
                    px: { xs: 1, sm: 1.2 },
                  }
                }}
              />
            )
          ))}
          
          {/* Form factors */}
          {filters.formFactors?.map(form => (
            <Chip
              key={form}
              label={form}
              size="small"
              onDelete={() => onFilterChange({
                target: { name: 'formFactors', checked: false, value: form }
              })}
              color="primary"
              variant="outlined"
              sx={{ 
                borderRadius: '0.375rem',
                height: { xs: '1.5rem', sm: '1.75rem' },
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                '& .MuiChip-deleteIcon': {
                  color: theme.palette.primary.main,
                  width: { xs: '1rem', sm: '1.125rem' },
                  height: { xs: '1rem', sm: '1.125rem' },
                },
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
                '& .MuiChip-label': {
                  px: { xs: 1, sm: 1.2 },
                }
              }}
            />
          ))}
          
          <Button
            size="small"
            startIcon={<ClearIcon fontSize="small" />}
            onClick={onResetFilters}
            sx={{ 
              textTransform: 'none', 
              ml: 'auto',
              fontSize: { xs: '0.7rem', sm: '0.75rem' },
              fontWeight: 500,
              color: theme.palette.text.secondary,
              borderRadius: '0.375rem',
              py: { xs: 0.3, sm: 0.4 },
              px: { xs: 1, sm: 1.5 },
              minWidth: { xs: '3.75rem', sm: '5rem' },
              '&:hover': {
                backgroundColor: alpha(theme.palette.error.main, 0.08),
                color: theme.palette.error.main,
              }
            }}
          >
            Clear all
          </Button>
        </Box>
      )}

      {/* Filters section */}
      <Box
        sx={{
          p: { xs: 1.5, sm: 2 },
          pt: { xs: 1.5, sm: 2 },
          pb: isMobile ? 7 : { xs: 1.5, sm: 2 },
        }}
      >
        <Grid container spacing={{ xs: 2, sm: 2.5 }}>
          {/* Quality Assurance */}
          <Grid item xs={12} sm={12} md={4}>
            <Box 
              sx={{ 
                p: { xs: 0, sm: 1.5 },
                backgroundColor: { xs: 'transparent', sm: alpha(theme.palette.background.paper, 0.6) },
                borderRadius: '0.5rem',
                height: '100%',
                border: { xs: 'none', sm: `0.0625rem solid ${alpha(theme.palette.divider, 0.1)}` }
              }}
            >
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  mb: { xs: 1, sm: 1.5 }, 
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  fontSize: { xs: '0.85rem', sm: '0.87rem' },
                  letterSpacing: '0.01em',
                  display: 'flex',
                  alignItems: 'center',
                  pb: { xs: 0, sm: 0.5 },
                  borderBottom: { xs: 'none', sm: `0.0625rem solid ${alpha(theme.palette.divider, 0.1)}` }
                }}
              >
                <CheckCircleOutlineIcon 
                  fontSize="small" 
                  sx={{ 
                    mr: 0.75,
                    color: theme.palette.primary.main,
                    fontSize: { xs: '1rem', sm: '1.1rem' }
                  }}
                />
                Quality Assurance
              </Typography>
              
              <Stack 
                spacing={{ xs: 0.5, sm: 0.75 }}
                sx={{ 
                  mt: { xs: 0, sm: 1 },
                  pl: { xs: 0, sm: 0.5 }
                }}
              >
                {/* Consistent checkbox group across devices */}
                {[
                  { name: 'third_party_tested', label: 'Third-Party Tested' },
                  { name: 'gmp_certified_facility', label: 'GMP Certified' },
                  { name: 'has_certifications', label: 'Has Certifications' }
                ].map(option => (
                  <FormControlLabel
                    key={option.name}
                    control={
                      <Checkbox
                        checked={!!filters[option.name]}
                        onChange={handleFilterChange}
                        name={option.name}
                        size={isMobile ? "medium" : "small"}
                        sx={{
                          color: alpha(theme.palette.primary.main, 0.7),
                          '&.Mui-checked': {
                            color: theme.palette.primary.main,
                          },
                          padding: { xs: '0.375rem', sm: '0.25rem' },
                        }}
                      />
                    }
                    label={
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: filters[option.name] ? 500 : 400,
                          color: filters[option.name] ? theme.palette.text.primary : theme.palette.text.secondary,
                          fontSize: { xs: '0.8rem', sm: '0.8rem' }
                        }}
                      >
                        {option.label}
                      </Typography>
                    }
                    sx={{ 
                      m: 0,
                      height: { xs: '2.25rem', sm: '2rem' },
                      transition: 'all 0.2s',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.action.hover, 0.05),
                      },
                      borderRadius: '0.25rem',
                      px: { xs: 0, sm: 0.5 }
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </Grid>

          {/* Form Factors & Dietary Options */}
          <Grid item xs={12} sm={12} md={8}>
            <Grid container spacing={{ xs: 2, sm: 2 }}>
              {/* Form Factor */}
              <Grid item xs={12} sm={6}>
                <Box 
                  sx={{ 
                    p: { xs: 0, sm: 1.5 },
                    backgroundColor: { xs: 'transparent', sm: alpha(theme.palette.background.paper, 0.6) },
                    borderRadius: '0.5rem',
                    height: '100%',
                    border: { xs: 'none', sm: `0.0625rem solid ${alpha(theme.palette.divider, 0.1)}` }
                  }}
                >
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      mb: { xs: 1, sm: 1.5 }, 
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      fontSize: { xs: '0.85rem', sm: '0.87rem' },
                      letterSpacing: '0.01em',
                      display: 'flex',
                      alignItems: 'center',
                      pb: { xs: 0, sm: 0.5 },
                      borderBottom: { xs: 'none', sm: `0.0625rem solid ${alpha(theme.palette.divider, 0.1)}` }
                    }}
                  >
                    <CategoryIcon 
                      fontSize="small" 
                      sx={{ 
                        mr: 0.75,
                        color: theme.palette.primary.main,
                        fontSize: { xs: '1rem', sm: '1.1rem' }
                      }}
                    />
                    Form Factor
                  </Typography>
                  
                  <Stack 
                    direction={{ xs: 'row', sm: 'column' }}
                    flexWrap="wrap"
                    spacing={{ xs: 0, sm: 0.75 }}
                    sx={{ 
                      mt: { xs: 0, sm: 1 },
                      pl: { xs: 0, sm: 0.5 }
                    }}
                  >
                    {FORM_FACTORS.map(form => (
                      <FormControlLabel
                        key={form}
                        control={
                          <Checkbox
                            checked={filters.formFactors?.includes(form) || false}
                            onChange={(e) => handleFilterChange({
                              target: {
                                name: 'formFactors',
                                checked: e.target.checked,
                                value: form
                              }
                            })}
                            size={isMobile ? "medium" : "small"}
                            sx={{
                              color: alpha(theme.palette.primary.main, 0.7),
                              '&.Mui-checked': {
                                color: theme.palette.primary.main,
                              },
                              padding: { xs: '0.375rem', sm: '0.25rem' },
                            }}
                          />
                        }
                        label={
                          <Tooltip
                            title={`Includes: ${getFormFactorVariants(form)}`}
                            arrow
                            placement="right"
                            enterTouchDelay={100}
                            leaveTouchDelay={1500}
                          >
                            <Typography 
                              variant="body2" 
                              sx={{
                                fontWeight: filters.formFactors?.includes(form) ? 500 : 400,
                                color: filters.formFactors?.includes(form) ? theme.palette.text.primary : theme.palette.text.secondary,
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'help',
                                fontSize: { xs: '0.8rem', sm: '0.8rem' },
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {isMobile ? form : form}
                            </Typography>
                          </Tooltip>
                        }
                        sx={{ 
                          m: 0,
                          height: { xs: '2.25rem', sm: '2rem' },
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.action.hover, 0.05),
                          },
                          borderRadius: '0.25rem',
                          px: { xs: 0, sm: 0.5 },
                          width: { xs: '50%', sm: '100%' }
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              </Grid>

              {/* Dietary Options */}
              <Grid item xs={12} sm={6}>
                <Box 
                  sx={{ 
                    p: { xs: 0, sm: 1.5 },
                    backgroundColor: { xs: 'transparent', sm: alpha(theme.palette.background.paper, 0.6) },
                    borderRadius: '0.5rem',
                    height: '100%',
                    border: { xs: 'none', sm: `0.0625rem solid ${alpha(theme.palette.divider, 0.1)}` }
                  }}
                >
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      mb: { xs: 1, sm: 1.5 }, 
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      fontSize: { xs: '0.85rem', sm: '0.87rem' },
                      letterSpacing: '0.01em',
                      display: 'flex',
                      alignItems: 'center',
                      pb: { xs: 0, sm: 0.5 },
                      borderBottom: { xs: 'none', sm: `0.0625rem solid ${alpha(theme.palette.divider, 0.1)}` }
                    }}
                  >
                    <LocalDiningIcon 
                      fontSize="small" 
                      sx={{ 
                        mr: 0.75,
                        color: theme.palette.primary.main,
                        fontSize: { xs: '1rem', sm: '1.1rem' }
                      }}
                    />
                    Dietary Options
                  </Typography>
                  
                  <Stack 
                    direction={{ xs: 'row', sm: 'column' }}
                    flexWrap="wrap"
                    spacing={{ xs: 0, sm: 0.75 }}
                    sx={{ 
                      mt: { xs: 0, sm: 1 },
                      pl: { xs: 0, sm: 0.5 }
                    }}
                  >
                    {DIETARY_OPTIONS.map(option => (
                      <FormControlLabel
                        key={option}
                        control={
                          <Checkbox
                            checked={filters[DIETARY_FIELD_MAP[option]] || false}
                            onChange={(e) => handleFilterChange({
                              target: {
                                name: 'dietaryOptions',
                                checked: e.target.checked,
                                value: option
                              }
                            })}
                            size={isMobile ? "medium" : "small"}
                            sx={{
                              color: alpha(theme.palette.primary.main, 0.7),
                              '&.Mui-checked': {
                                color: theme.palette.primary.main,
                              },
                              padding: { xs: '0.375rem', sm: '0.25rem' },
                            }}
                          />
                        }
                        label={
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: filters[DIETARY_FIELD_MAP[option]] ? 500 : 400,
                              color: filters[DIETARY_FIELD_MAP[option]] ? theme.palette.text.primary : theme.palette.text.secondary,
                              fontSize: { xs: '0.8rem', sm: '0.8rem' }
                            }}
                          >
                            {option}
                          </Typography>
                        }
                        sx={{ 
                          m: 0,
                          height: { xs: '2.25rem', sm: '2rem' },
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.action.hover, 0.05),
                          },
                          borderRadius: '0.25rem',
                          px: { xs: 0, sm: 0.5 },
                          width: { xs: '50%', sm: '100%' }
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Use consistent "Done" button that calls handleClose */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mt: 3,
            position: isMobile ? 'sticky' : 'relative',
            bottom: isMobile ? 0 : 'auto',
            left: isMobile ? 0 : 'auto',
            right: isMobile ? 0 : 'auto',
            p: isMobile ? 1.5 : 0,
            borderTop: isMobile ? `0.0625rem solid ${alpha(theme.palette.divider, 0.1)}` : 'none',
            backgroundColor: isMobile ? theme.palette.background.paper : 'transparent',
            zIndex: isMobile ? 1 : 'auto',
            width: isMobile ? '100%' : 'auto',
          }}
        >
          <Button
            variant={isMobile ? "contained" : "outlined"}
            endIcon={
              <KeyboardArrowUpIcon 
                fontSize="small" 
                sx={{ fontSize: isMobile ? '1rem' : '0.8rem' }} 
              />
            }
            onClick={handleClose}
            size="small"
            sx={{
              borderRadius: '0.375rem',
              py: isMobile ? 0.75 : 0.25,
              px: isMobile ? 2 : 1.5,
              textTransform: 'none',
              fontWeight: 500,
              boxShadow: isMobile ? 1 : 0,
              width: isMobile ? '100%' : 'auto',
              color: isMobile ? undefined : theme.palette.primary.main,
              borderColor: isMobile ? undefined : theme.palette.primary.main,
              fontSize: { xs: '0.85rem', sm: '0.75rem' },
              minHeight: isMobile ? '2.25rem' : '1.75rem',
              '&:hover': {
                backgroundColor: isMobile 
                  ? theme.palette.primary.dark 
                  : alpha(theme.palette.primary.main, 0.08),
              }
            }}
          >
            Done
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

// Helper function to count active filters
export const getFilterCount = (filters) => {
  let count = 0;
  
  // Count quality assurance filters
  if (filters.third_party_tested) count++;
  if (filters.gmp_certified_facility) count++;
  if (filters.has_certifications) count++;
  
  // Count dietary filters
  Object.values(DIETARY_FIELD_MAP).forEach(fieldName => {
    if (filters[fieldName]) count++;
  });
  
  // Count form factors
  count += (filters.formFactors?.length || 0);
  
  return count;
};

// Helper function to apply filters to products
export const applyFilters = (products, filters) => {
  if (!products || products.length === 0) return [];
  
  let result = [...products];
  
  // Apply quality assurance filters
  if (filters.third_party_tested) {
    result = result.filter(product => 
      (product.testing_3rd_party && product.testing_3rd_party.length > 0) || 
      product.third_party_tested === true
    );
  }
  
  if (filters.gmp_certified_facility) {
    result = result.filter(product => 
      product.gmp_certified_facility === true ||
      (product.other_certifications &&
        product.other_certifications.some(cert =>
          typeof cert === 'string' && (
            cert.toLowerCase().includes('gmp') ||
            cert.toLowerCase().includes('good manufacturing')
          )
        )
      )
    );
  }
  
  // Add filter for has_certifications
  if (filters.has_certifications) {
    result = result.filter(product =>
      product.other_certifications && Array.isArray(product.other_certifications) && product.other_certifications.length > 0
    );
  }
  
  // Apply dietary filters
  Object.entries(DIETARY_FIELD_MAP).forEach(([label, fieldName]) => {
    if (filters[fieldName]) {
      result = result.filter(product => {
        // Handle Vegetarian/Vegan specifically
        if (label === 'Vegetarian/Vegan') {
          // Check direct boolean property first
          if (product.vegetarian === true) return true;
          // Check if 'vegan' appears in certifications
          if (product.other_certifications && Array.isArray(product.other_certifications)) {
            return product.other_certifications.some(cert =>
              typeof cert === 'string' && cert.toLowerCase().includes('vegan')
            );
          }
          // Check dietary_certifications as well (if exists)
          if (product.dietary_certifications && Array.isArray(product.dietary_certifications)) {
             return product.dietary_certifications.some(cert =>
               typeof cert === 'string' && cert.toLowerCase().includes('vegan')
             );
           }
          return false; // If vegetarian field is not true and 'vegan' not found
        }
        
        // Handle other dietary filters (Gluten-Free, Non-GMO, Soy-Free)
        // Check direct boolean property
        if (product[fieldName] === true) return true;
        
        // Fallback checks in certifications (using simplified logic)
        const term = label.toLowerCase().split('-')[0]; // e.g., 'gluten', 'non', 'soy'
        // Check dietary_certifications array
        if (product.dietary_certifications && Array.isArray(product.dietary_certifications)) {
          if (product.dietary_certifications.some(cert => typeof cert === 'string' && cert.toLowerCase().includes(term))) return true;
        }
        // Check in other_certifications array as fallback
        if (product.other_certifications && Array.isArray(product.other_certifications)) {
           if (product.other_certifications.some(cert => typeof cert === 'string' && cert.toLowerCase().includes(term))) return true;
        }
        
        return false;
      });
    }
  });
  
  // Apply form factor filters - Simplified matching
  if (filters.formFactors?.length > 0) {
    result = result.filter(product => {
      if (!product.form_factor || typeof product.form_factor !== 'string') return false;
      
      const productFormLower = product.form_factor.toLowerCase().trim();
      
      return filters.formFactors.some(selectedForm => {
        // Find the canonical key for the selected form (e.g., 'Chewables' -> 'chewables')
        const canonicalKey = Object.keys(FORM_FACTOR_DISPLAY).find(
           key => key === selectedForm // Match directly with the selected UI form name
        )?.toLowerCase();

        if (!canonicalKey) return false; // Should not happen if constants are correct

        const validVariations = FORM_FACTOR_MAPPINGS[canonicalKey] || [];

        // Check if the product's form factor contains any of the valid variations
        // This makes powder matching more lenient.
        return validVariations.some(variation => productFormLower.includes(variation.toLowerCase()));
      });
    });
  }

  return result;
};

// Default filter state that can be used across components
export const DEFAULT_FILTERS = {
  // Quality assurance
  third_party_tested: false,
  gmp_certified_facility: false,
  has_certifications: false,
  
  // Dietary options (updated based on DIETARY_FIELD_MAP)
  vegetarian: false,
  non_gmo: false,
  gluten_free: false,
  soy_free: false,
  
  // Form factors
  formFactors: []
};

export default ProductFilters; 